import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import useSeo from '../hooks/useSeo';
import Footer from '../components/Footer';
import { 
  Users, Rocket, GraduationCap, TrendingUp,
  Mail, ArrowRight, Target
} from 'lucide-react';

const CareersPage = () => {
  useSeo({
    title: 'Careers at KnowMySchemes — Build the Future of Indian Govt-Tech',
    description: 'Join KnowMySchemes — we are building India\'s most comprehensive citizen-services platform. View open roles in engineering, product, design, and content.',
    canonical: 'https://knowmyschemes.com/careers',
  });
  const benefits = [
    { icon: Users, text: 'Collaborative work environment' },
    { icon: Rocket, text: 'Make a real social impact' },
    { icon: GraduationCap, text: 'Training & skill development' },
    { icon: TrendingUp, text: 'Growth opportunities' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-600 to-green-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="text-center">
            <p className="text-green-200 text-sm font-medium mb-2">Career Opportunities</p>
            <h1 className="text-3xl md:text-5xl font-bold mb-6">
              Build your career with KMS
            </h1>
            <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto">
              Join our mission to simplify welfare access for every Indian citizen. We're preparing to expand our team soon.
            </p>
          </div>
        </div>
      </div>

      {/* Hiring Status Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center border border-gray-100">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Target className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
            Hiring Will Be Opened Soon
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            We're currently preparing exciting opportunities to join the KMS team. Our hiring process will begin shortly.
          </p>
          
          {/* Expected Role */}
          <div className="bg-green-50 rounded-xl p-6 mb-8">
            <p className="text-green-700 font-medium mb-2">Expected Role:</p>
            <h3 className="text-xl font-bold text-green-800">Operations Support Associate</h3>
          </div>

          {/* Contact for notifications */}
          <div className="mb-8">
            <p className="text-gray-600 mb-3">Want to be notified?</p>
            <a 
              href="mailto:careers@knowmyschemes.com"
              className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold text-lg"
            >
              <Mail className="w-5 h-5" />
              careers@knowmyschemes.com
            </a>
          </div>
        </div>
      </div>

      {/* Why Join Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Why join KMS?
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-gray-50 hover:bg-green-50 transition-colors">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-gray-700 font-medium">{benefit.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Quote */}
      <div className="bg-green-700 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-green-200 text-sm font-medium mb-2">Our Mission</p>
          <p className="text-xl md:text-2xl font-medium">
            To ensure no eligible citizen misses out on government welfare benefits.
          </p>
        </div>
      </div>

      {/* Learn More Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
          While you wait, learn more about us
        </h2>
        <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
          Discover our mission, values, and how we're working to help citizens access welfare benefits.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link 
            to="/about"
            className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors"
          >
            About KMS
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link 
            to="/future"
            className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-green-700 border-2 border-green-600 px-8 py-4 rounded-xl font-semibold transition-colors"
          >
            Our Future Vision
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CareersPage;
